import { Subscription } from '@rails/actioncable';
import { getCableConsumer } from './CableClient';
import type {
  NextGameCreatedMessage,
  RoundReadyStateMessage,
  ChatMessageEvent,
  ChatHistoryEvent,
  ErrorMessage,
  SessionAbandonedMessage,
  SessionChannelMessage,
  LobbyUpdatedMessage,
  LobbyStateMessage,
  ParticipantRemovedMessage,
} from '../types/game';

type MessageCallback<T> = (data: T) => void;

export class SessionChannel {
  private subscription: Subscription | null = null;
  private sessionId: number;

  private _everConnected = false;

  private _onNextGameCreated: MessageCallback<NextGameCreatedMessage> | null = null;
  private _onRoundReadyState: MessageCallback<RoundReadyStateMessage> | null = null;
  private _onChatMessage: MessageCallback<ChatMessageEvent> | null = null;
  private _onChatHistory: MessageCallback<ChatHistoryEvent> | null = null;
  private _onError: MessageCallback<ErrorMessage> | null = null;
  private _onSessionAbandoned: MessageCallback<SessionAbandonedMessage> | null = null;
  private _onLobbyUpdated: MessageCallback<LobbyUpdatedMessage> | null = null;
  private _onLobbyState: MessageCallback<LobbyStateMessage> | null = null;
  private _onParticipantRemoved: MessageCallback<ParticipantRemovedMessage> | null = null;
  private _onReconnect: (() => void) | null = null;
  private _onRejected: (() => void) | null = null;

  constructor(sessionId: number) {
    this.sessionId = sessionId;
  }

  async subscribe(): Promise<void> {
    const consumer = await getCableConsumer();

    this.subscription = consumer.subscriptions.create(
      { channel: 'SessionChannel', session_id: this.sessionId },
      {
        received: (data: unknown) => {
          this.handleMessage(data as SessionChannelMessage);
        },
        connected: () => {
          if (this._everConnected) {
            console.log(`[SessionChannel] RECONNECTED session=${this.sessionId}`);
            this._onReconnect?.();
          } else {
            console.log(`[SessionChannel] CONNECTED session=${this.sessionId}`);
            this._everConnected = true;
          }
        },
        disconnected: () => {
          console.log(`[SessionChannel] DISCONNECTED session=${this.sessionId}`);
        },
        rejected: () => {
          console.error(`[SessionChannel] REJECTED session=${this.sessionId} — check auth token and session membership`);
          this._onRejected?.();
        },
      }
    );
  }

  private handleMessage(data: SessionChannelMessage): void {
    switch (data.type) {
      case 'next_game_created':
        this._onNextGameCreated?.(data);
        break;
      case 'round_ready_state':
        this._onRoundReadyState?.(data);
        break;
      case 'chat_message':
        this._onChatMessage?.(data);
        break;
      case 'chat_history':
        this._onChatHistory?.(data);
        break;
      case 'error':
        console.warn(`[SessionChannel] error from server: ${data.message}`);
        this._onError?.(data);
        break;
      case 'session_abandoned':
        this._onSessionAbandoned?.(data);
        break;
      case 'lobby_updated':
        this._onLobbyUpdated?.(data);
        break;
      case 'lobby_state':
        this._onLobbyState?.(data);
        break;
      case 'participant_removed':
        this._onParticipantRemoved?.(data);
        break;
      default:
        console.warn('[SessionChannel] unhandled message type:', (data as { type: string }).type);
    }
  }

  // ─── Register Callbacks ──────────────────────────────────

  onNextGameCreated(callback: MessageCallback<NextGameCreatedMessage>): this {
    this._onNextGameCreated = callback;
    return this;
  }

  /** Round-end ready gate: authoritative readiness snapshots while a finished
   *  round waits to advance. Non-authoritative for the transition itself. */
  onRoundReadyState(callback: MessageCallback<RoundReadyStateMessage>): this {
    this._onRoundReadyState = callback;
    return this;
  }

  onChatMessage(callback: MessageCallback<ChatMessageEvent>): this {
    this._onChatMessage = callback;
    return this;
  }

  onChatHistory(callback: MessageCallback<ChatHistoryEvent>): this {
    this._onChatHistory = callback;
    return this;
  }

  onError(callback: MessageCallback<ErrorMessage>): this {
    this._onError = callback;
    return this;
  }

  onSessionAbandoned(callback: MessageCallback<SessionAbandonedMessage>): this {
    this._onSessionAbandoned = callback;
    return this;
  }

  /** Lobby v1: authoritative lobby snapshot pushed on every seat/ready/theme change. */
  onLobbyUpdated(callback: MessageCallback<LobbyUpdatedMessage>): this {
    this._onLobbyUpdated = callback;
    return this;
  }

  /** Lobby v1: lobby snapshot transmitted on (re)subscribe (reconnect restore). */
  onLobbyState(callback: MessageCallback<LobbyStateMessage>): this {
    this._onLobbyState = callback;
    return this;
  }

  /** Targeted: the host removed a participant. Only the removed client acts. */
  onParticipantRemoved(callback: MessageCallback<ParticipantRemovedMessage>): this {
    this._onParticipantRemoved = callback;
    return this;
  }

  onReconnect(callback: () => void): this {
    this._onReconnect = callback;
    return this;
  }

  /** Called when the server REJECTS the subscription (membership/auth failure). */
  onRejected(callback: () => void): this {
    this._onRejected = callback;
    return this;
  }

  // ─── Send Actions ────────────────────────────────────────

  sendMessage(body: string): void {
    if (!this.subscription) {
      console.error('[SessionChannel] Not subscribed — cannot send message');
      return;
    }
    this.subscription.perform('send_message', { body });
  }

  fetchHistory(): void {
    if (!this.subscription) return;
    this.subscription.perform('fetch_history', {});
  }

  nextGame(): void {
    if (!this.subscription) {
      console.error('[SessionChannel] Not subscribed — cannot request next game');
      return;
    }
    this.subscription.perform('next_game', {});
  }

  // ─── Lifecycle ───────────────────────────────────────────

  unsubscribe(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    this._onNextGameCreated = null;
    this._onRoundReadyState = null;
    this._onChatMessage = null;
    this._onChatHistory = null;
    this._onError = null;
    this._onSessionAbandoned = null;
    this._onLobbyUpdated = null;
    this._onLobbyState = null;
    this._onParticipantRemoved = null;
    this._onReconnect = null;
    this._onRejected = null;
  }
}
